import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../contexts/TranslationContext';

/**
 * ModalShell — THE overlay + box shell for DT modals (create ticket, tickets for an asset,
 * add IoT device, device editor, connector editor). Owns the dimmed backdrop, click-outside
 * and Escape to close, focus, and the surface box; content and buttons stay with the caller.
 *
 * IT RENDERS INTO document.body. A z-index only competes inside its stacking context, and
 * this shell is opened from inside the persistent-viewers wrapper (its own stacking
 * context), so a dialog mounted in place was trapped there however high its number: it
 * opened UNDER the element panel that launched it, and the app header stayed bright over
 * its backdrop. From the body it takes the page-level dialog layer (--dt-z-modal) and sits
 * above the header, the sidebar and every floating panel.
 *
 * THE MODAL DIALOG PATTERN, as WAI-ARIA describes it:
 *   * everything behind is dimmed and unclickable — the element panel that opened the
 *     dialog stays where it was, under the backdrop, so nobody loses their place;
 *   * focus moves INTO the dialog when it opens, and goes back to whatever had it —
 *     usually the button that opened it — when it closes;
 *   * Escape closes it, exactly as a click on the backdrop does, and only when that is
 *     allowed (`dismissible`): a dialog that is saving must not vanish mid-request.
 */
interface ModalShellProps {
  onClose: () => void;
  ariaLabel?: string;
  dismissible?: boolean;
  width?: number;
  /** Fraction of viewport height the box may use before scrolling. */
  maxHeight?: string;
  paddingTop?: number;
  children: React.ReactNode;
}

const ModalShell: React.FC<ModalShellProps> = ({ onClose, ariaLabel, dismissible = true, width = 460, maxHeight = '78%', paddingTop = 70, children }) => {
  const { colors } = useTheme();
  const { isRTL } = useTranslation();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Read through refs so the effects below run once per opening, not on every render —
  // a parent that re-creates onClose each render must not steal focus back repeatedly.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  // The element to return focus to, captured on the FIRST RENDER — before the dialog's own
  // children mount. By the time an effect runs, a field with autoFocus has already taken
  // focus, and reading document.activeElement then would record the field, not the button
  // that opened the dialog.
  const openerRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
  );

  useEffect(() => {
    const opener = openerRef.current;
    // Into the dialog — unless a field already took focus on purpose (autoFocus), which
    // knows better than a generic "first control" where the person should start.
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      const first = dialog.querySelector<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      (first || dialog).focus({ preventScroll: true });
    }

    const onKey = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (dialogs[dialogs.length - 1] !== dialog) return;
      if (event.key === 'Tab' && dialog) {
        const controls = Array.from(dialog.querySelectorAll<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )).filter(el => !el.matches(':disabled') && el.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); dialog.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
        return;
      }
      if (event.key !== 'Escape' || !dismissibleRef.current) return;
      // Handled here and nowhere else: a dialog is the topmost thing, so Escape belongs to it.
      event.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Back to where the person was — only if that element still exists.
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 'var(--dt-z-modal)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop,
      }}
      onClick={() => { if (dismissible) onClose(); }}
    >
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={ariaLabel} tabIndex={-1}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          width, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', maxHeight, overflow: 'auto', background: colors.surface,
          border: `1px solid ${colors.border}`, borderRadius: 8, padding: 18, outline: 'none',
        }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default ModalShell;
